// src/components/PricingPage.jsx  v2
//
// Audience: authenticated KFX user already inside the app.
// Job: help them decide to upgrade from Free → Pro or Lifetime.
//
// Architecture:
//   Header (account context + current plan)
//   Plan cards (3-col grid, Pro featured)
//   Comparison table (accordion by category)
//   FAQ
//
// Removed from v1:
//   • Hero section with marketing headline (belongs on LandingPage)
//   • Testimonials (belongs on LandingPage)
//   • Trust / security badges (belongs on LandingPage)
//   • Final CTA banner (belongs on LandingPage)
//   • "No credit card" reassurances (user already has an account)
//   • Scarcity bar (dark pattern, not appropriate for signed-in users)

import { useState } from 'react';
import { api } from '../utils/api';

/* ─── Design tokens ─────────────────────────────────────────── */
const T = {
  bg:          '#0A0A0C',
  dark2:       'var(--dark2, #111113)',
  dark3:       'var(--dark3, #18181B)',
  border:      'rgba(255,255,255,.06)',
  borderSub:   'rgba(255,255,255,.03)',
  gold:        'var(--gold, #C9A84C)',
  goldRgb:     '201,168,76',
  goldDim:     'rgba(201,168,76,.10)',
  goldBorder:  'rgba(201,168,76,.25)',
  goldLabel:   'rgba(201,168,76,.55)',
  green:       '#3DBE73',
  greenRgb:    '46,204,138',
  text:        'rgba(240,234,214,.92)',
  textSub:     'rgba(200,192,168,.65)',
  muted:       'rgba(200,192,168,.38)',
  faint:       'rgba(200,192,168,.18)',
  red:         '#D94F4F',
};

/* ─── CSS ───────────────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800;900&display=swap');

  @keyframes fadeUp   { from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:translateY(0);} }
  @keyframes spin     { to{transform:rotate(360deg);} }
  @keyframes slideDown{ from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:translateY(0);} }
  @keyframes shimPass { from{transform:translateX(-100%)}to{transform:translateX(300%)} }

  .kfx-up-page {
    animation: fadeUp .35s ease both;
    font-family: 'Inter', sans-serif;
    -webkit-font-smoothing: antialiased;
  }

  /* Plan cards */
  .kfx-plan-card {
    transition: transform .22s cubic-bezier(.4,0,.2,1), box-shadow .22s, border-color .2s;
  }
  .kfx-plan-card:hover { transform: translateY(-4px) !important; }
  .kfx-plan-card.featured { animation: goldPulse 3.5s ease-in-out infinite; }
  @keyframes goldPulse {
    0%,100%{ box-shadow: 0 0 0 1px rgba(201,168,76,.22), 0 16px 48px rgba(0,0,0,.55); }
    55%    { box-shadow: 0 0 0 1px rgba(201,168,76,.40), 0 20px 60px rgba(201,168,76,.08); }
  }

  /* CTA buttons */
  .kfx-btn-primary {
    transition: filter .15s, transform .15s, box-shadow .15s;
  }
  .kfx-btn-primary:hover {
    filter: brightness(1.1);
    transform: translateY(-1px);
    box-shadow: 0 8px 28px rgba(201,168,76,.36) !important;
  }
  .kfx-btn-ghost {
    transition: background .15s, border-color .15s, color .15s;
  }
  .kfx-btn-ghost:hover {
    background: rgba(201,168,76,.10) !important;
    border-color: rgba(201,168,76,.35) !important;
    color: rgba(240,234,214,.9) !important;
  }
  .kfx-btn-outline {
    transition: background .15s, border-color .15s, color .15s;
  }
  .kfx-btn-outline:hover {
    background: rgba(255,255,255,.04) !important;
    border-color: rgba(255,255,255,.14) !important;
  }

  /* Feature rows */
  .kfx-feat-row {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255,255,255,.035);
  }
  .kfx-feat-row:last-child { border-bottom: none; }

  /* Table */
  .kfx-tbl-row { transition: background .12s; cursor: default; }
  .kfx-tbl-row:hover { background: rgba(201,168,76,.03) !important; }

  /* FAQ */
  .kfx-faq-item { transition: background .15s; cursor: pointer; }
  .kfx-faq-item:hover { background: rgba(255,255,255,.015) !important; }

  /* Waitlist input */
  .kfx-wl-input {
    background: rgba(255,255,255,.04);
    border: 1px solid rgba(255,255,255,.09);
    border-radius: 6px;
    color: rgba(240,234,214,.9);
    font-size: 13px; padding: 10px 13px;
    width: 100%; box-sizing: border-box;
    outline: none;
    transition: border-color .15s;
    font-family: 'Inter', sans-serif;
  }
  .kfx-wl-input::placeholder { color: rgba(200,192,168,.28); }
  .kfx-wl-input:focus { border-color: rgba(201,168,76,.45); }

  /* Responsive */
  @media(max-width:960px) {
    .kfx-plans-grid { grid-template-columns: 1fr 1fr !important; }
    .kfx-plan-featured { grid-column: 1/-1 !important; max-width: 460px !important; margin: 0 auto !important; }
  }
  @media(max-width:640px) {
    .kfx-plans-grid { grid-template-columns: 1fr !important; }
    .kfx-plan-featured { grid-column: auto !important; max-width: 100% !important; }
    .kfx-tbl-cols { grid-template-columns: 1.6fr .8fr .8fr .8fr !important; font-size: 11px !important; }
    .kfx-tbl-wrap { overflow-x: auto !important; -webkit-overflow-scrolling: touch !important; }
    .kfx-plan-card:hover { transform: none !important; }
  }
`;

/* ─── Primitives ────────────────────────────────────────────── */
function Spinner() {
  return (
    <div style={{
      width: 13, height: 13, flexShrink: 0,
      border: '2px solid rgba(255,255,255,.1)',
      borderTop: `2px solid ${T.gold}`,
      borderRadius: '50%',
      animation: 'spin .65s linear infinite',
    }}/>
  );
}

function CheckIcon({ size = 14, dimmed = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink:0, marginTop:1, opacity: dimmed ? .28 : 1 }}>
      <circle cx="7" cy="7" r="7" fill="rgba(201,168,76,.12)"/>
      <path d="M4 7l2.2 2.2L10 4.8" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function CrossIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink:0, marginTop:1 }}>
      <circle cx="7" cy="7" r="7" fill="rgba(255,255,255,.03)"/>
      <path d="M4.5 9.5l5-5M9.5 9.5l-5-5" stroke="rgba(200,192,168,.18)" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}

function GreenCheckIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink:0, marginTop:1 }}>
      <circle cx="7" cy="7" r="7" fill="rgba(46,204,138,.12)"/>
      <path d="M4 7l2.2 2.2L10 4.8" stroke="#3DBE73" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ─── Plan data ─────────────────────────────────────────────── */
const PLANS = [
  {
    id:          'free',
    name:        'Free',
    price:       '$0',
    period:      'forever',
    featured:    false,
    description: 'Full access while we grow. Everything works — no time limit.',
    badge:       'YOUR CURRENT PLAN',
    badgeColor:  T.muted,
    features: [
      'Unlimited trade logging',
      'Full analytics dashboard',
      'MT4 / MT5 CSV import',
      'Execution grading (A / B / C)',
      'Trading notebook & debrief',
      'Rule adherence tracker',
      'What-If trade simulator',
      'Session heatmap & equity curve',
      'Prop firm tracker',
      'Drawdown & position sizing tools',
      { text: 'AI coaching report', note: '1 / month' },
    ],
  },
  {
    id:          'pro',
    name:        'Pro',
    price:       '$4.99',
    period:      '/ month',
    featured:    true,
    description: 'Unlimited AI coaching and weekly pattern alerts for traders who review every session.',
    badge:       'MOST POPULAR',
    badgeColor:  T.gold,
    cta:         'Get Early Access',
    features: [
      'Everything in Free',
      'Unlimited AI coaching reports',
      'AI pattern detection alerts',
      'Weekly AI performance digest',
      'Advanced session breakdowns',
      'PDF report export',
      'Priority email support',
      'Early access to new features',
      { text: 'MT4 / MT5 auto-sync', note: 'Coming soon' },
    ],
  },
  {
    id:          'lifetime',
    name:        'Lifetime',
    price:       '$49',
    period:      'one-time',
    featured:    false,
    description: 'Pay once. Founding member pricing — this price closes when we exit early access.',
    badge:       'BEST VALUE',
    badgeColor:  T.green,
    cta:         'Lock In Lifetime',
    features: [
      'Everything in Pro',
      'Lifetime access — pay once',
      'All future features included',
      'Founding member badge',
      'Priority support forever',
    ],
  },
];

/* ─── Comparison table data ─────────────────────────────────── */
const TABLE = [
  {
    category: 'Core',
    rows: [
      { f: 'Unlimited trade logging',      free: true,    pro: true,  life: true  },
      { f: 'Full analytics dashboard',     free: true,    pro: true,  life: true  },
      { f: 'MT4 / MT5 CSV import',         free: true,    pro: true,  life: true  },
      { f: 'Execution grading',            free: true,    pro: true,  life: true  },
      { f: 'What-If trade simulator',      free: true,    pro: true,  life: true  },
      { f: 'Prop firm tracker',            free: true,    pro: true,  life: true  },
    ],
  },
  {
    category: 'AI Coach',
    rows: [
      { f: 'AI coaching reports',          free: '1 / mo', pro: '∞',  life: '∞'  },
      { f: 'AI pattern detection',         free: false,   pro: true,  life: true  },
      { f: 'Weekly performance digest',    free: false,   pro: true,  life: true  },
    ],
  },
  {
    category: 'Export & Support',
    rows: [
      { f: 'PDF report export',            free: false,   pro: true,  life: true  },
      { f: 'Priority email support',       free: false,   pro: true,  life: true  },
    ],
  },
  {
    category: 'Integrations',
    rows: [
      { f: 'MT4 / MT5 auto-sync',          free: false,   pro: 'Soon', life: 'Soon' },
    ],
  },
  {
    category: 'Lifetime Exclusive',
    rows: [
      { f: 'All future updates included',  free: false,   pro: false, life: true  },
      { f: 'Founding member badge',        free: false,   pro: false, life: true  },
      { f: 'Price locked forever',         free: false,   pro: false, life: true  },
    ],
  },
];

/* ─── FAQ data ──────────────────────────────────────────────── */
const FAQS = [
  {
    q: 'When do Pro and Lifetime launch?',
    a: "We're in early access — all features are free right now while we grow. Pro and Lifetime launch when we exit early access. Early waitlist users get first access and locked pricing.",
  },
  {
    q: 'Will my data be safe when plans change?',
    a: 'Yes. All your trades, journals, and reports are yours and persist regardless of plan changes. You can export everything at any time.',
  },
  {
    q: 'Is the AI coach unlimited on Pro?',
    a: 'Yes. Free gets 1 report per month. Pro removes that limit entirely — run a fresh analysis after every session.',
  },
  {
    q: 'Can I cancel Pro anytime?',
    a: 'Yes, cancel any time — no questions asked. Your data stays accessible on the Free tier.',
  },
  {
    q: 'Does this work with prop firm accounts?',
    a: 'Yes. The prop firm tracker is built for FTMO, MyForexFunds, The5ers, and similar challenges — daily drawdown, consistency rules, target progress.',
  },
  {
    q: 'When is MT4 / MT5 auto-sync launching?',
    a: "It's in development as a Pro feature. Once live, you'll connect an MT4/MT5 account with a read-only investor password and your closed trades will populate the journal automatically. Join the Pro waitlist to get notified the moment it ships.",
  },
];

/* ─── TableCell ─────────────────────────────────────────────── */
function TableCell({ val, plan }) {
  const isLife = plan === 'life';
  if (val === true)  return <div style={{ display:'flex', justifyContent:'center' }}>{isLife ? <GreenCheckIcon size={13}/> : <CheckIcon size={13}/>}</div>;
  if (val === false) return <div style={{ display:'flex', justifyContent:'center' }}><CrossIcon size={13}/></div>;
  return (
    <div style={{ textAlign:'center' }}>
      <span style={{
        fontSize: 10, fontWeight: 700,
        color: T.gold,
        background: 'rgba(201,168,76,.10)',
        padding: '2px 7px', borderRadius: 3,
        letterSpacing: '.03em',
      }}>{val}</span>
    </div>
  );
}

/* ─── FAQ item ──────────────────────────────────────────────── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="kfx-faq-item"
      onClick={() => setOpen(o => !o)}
      style={{
        borderBottom: `1px solid ${T.border}`,
        padding: '18px 16px',
        borderRadius: open ? 6 : 0,
        userSelect: 'none',
      }}
    >
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:16 }}>
        <span style={{ fontSize:13.5, fontWeight:600, color: T.text, lineHeight:1.4 }}>{q}</span>
        <span style={{
          fontSize: 18, color: T.gold, flexShrink: 0,
          transition: 'transform .2s cubic-bezier(.4,0,.2,1)',
          transform: open ? 'rotate(45deg)' : 'none',
          fontWeight: 300, lineHeight: 1, opacity: .7,
        }}>+</span>
      </div>
      {open && (
        <p style={{
          fontSize: 13, color: T.textSub, lineHeight: 1.85,
          margin: '12px 0 0', animation: 'slideDown .18s ease both',
          paddingRight: 24,
        }}>
          {a}
        </p>
      )}
    </div>
  );
}

/* ─── PlanCard ──────────────────────────────────────────────── */
function PlanCard({ plan, currentPlan = 'free' }) {
  const [email,     setEmail]     = useState('');
  const [wlStep,    setWlStep]    = useState('idle'); // idle | input | loading | done | info
  const [wlErr,     setWlErr]     = useState(null);
  const [wlInfoMsg, setWlInfoMsg] = useState('');

  const isCurrent  = currentPlan === plan.id;
  const isLocked   = plan.id !== 'free'; // Pro & Lifetime on waitlist until launch
  const isPro      = plan.id === 'pro';
  const isLifetime = plan.id === 'lifetime';

  async function handleWaitlist() {
    if (wlStep === 'idle') { setWlStep('input'); return; }
    const trimmed = email.trim();
    if (!trimmed || !/\S+@\S+\.\S+/.test(trimmed)) {
      setWlErr('Enter a valid email.'); return;
    }
    setWlStep('loading'); setWlErr(null);
    try {
      const data = await api('/waitlist', { method:'POST', body: JSON.stringify({ email:trimmed, plan:plan.name }) });
      if (data?.alreadyOnList) { setWlInfoMsg("You're already on the list — we'll notify you at launch."); setWlStep('info'); }
      else setWlStep('done');
    } catch(e) {
      setWlErr(e?.data?.message || 'Something went wrong. Try again.');
      setWlStep('input');
    }
  }

  /* Badge config */
  const badgeStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 9, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
    padding: '3px 9px', borderRadius: 4,
  };
  const planBadge = isCurrent
    ? { ...badgeStyle, color: T.muted, background:'rgba(255,255,255,.04)', border:`1px solid rgba(255,255,255,.07)` }
    : isPro
    ? { ...badgeStyle, color: T.gold,  background:'rgba(201,168,76,.10)', border:`1px solid rgba(201,168,76,.26)` }
    : isLifetime
    ? { ...badgeStyle, color: T.green, background:'rgba(46,204,138,.08)', border:`1px solid rgba(46,204,138,.22)` }
    : { ...badgeStyle, color: T.muted, background:'rgba(255,255,255,.04)', border:`1px solid rgba(255,255,255,.07)` };

  return (
    <div
      className={`kfx-plan-card${plan.featured ? ' featured kfx-plan-featured' : ''}`}
      style={{
        background: plan.featured ? T.dark3 : T.dark2,
        border: `1px solid ${
          plan.featured ? 'rgba(201,168,76,.28)'
          : isLifetime  ? 'rgba(46,204,138,.16)'
          : T.border
        }`,
        borderRadius: 14,
        padding: plan.featured ? '30px 26px 26px' : '24px 22px',
        display: 'flex', flexDirection: 'column',
        position: 'relative', overflow: 'hidden',
        transform: plan.featured ? 'scale(1.02)' : 'none',
        zIndex: plan.featured ? 1 : 0,
      }}
    >
      {/* Top accent line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: plan.featured ? 2 : 1,
        background: plan.featured
          ? `linear-gradient(90deg, transparent 0%, rgba(201,168,76,.9) 40%, rgba(201,168,76,.9) 60%, transparent 100%)`
          : isLifetime
          ? `linear-gradient(90deg, transparent, rgba(46,204,138,.25), transparent)`
          : `linear-gradient(90deg, transparent, rgba(255,255,255,.06), transparent)`,
      }}/>

      {/* Ambient glow — Pro only */}
      {plan.featured && (
        <div style={{
          position:'absolute', top:-70, left:'50%', transform:'translateX(-50%)',
          width:280, height:180,
          background:'radial-gradient(ellipse, rgba(201,168,76,.07) 0%, transparent 70%)',
          pointerEvents:'none',
        }}/>
      )}

      {/* Plan badge */}
      <div style={{ marginBottom: 16 }}>
        <span style={planBadge}>
          {isCurrent && '✓ '}
          {plan.badge}
        </span>
      </div>

      {/* Plan name + price */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: plan.featured ? 28 : 24,
          letterSpacing: '.06em',
          color: plan.featured ? T.gold : isLifetime ? T.green : T.text,
          lineHeight: 1,
          marginBottom: 6,
        }}>
          {plan.name}
        </div>
        <div style={{ display:'flex', alignItems:'baseline', gap:5 }}>
          <span style={{
            fontSize: plan.featured ? 36 : 30,
            fontWeight: 800,
            color: T.text,
            letterSpacing: '-.03em',
            lineHeight: 1,
          }}>{plan.price}</span>
          <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>{plan.period}</span>
        </div>
      </div>

      {/* Description */}
      <p style={{
        fontSize: 12.5, color: T.textSub, lineHeight: 1.65,
        margin: '0 0 20px',
      }}>
        {plan.description}
      </p>

      {/* CTA area */}
      <div style={{ marginBottom: 22 }}>
        {isCurrent ? (
          <div style={{
            padding: '11px', borderRadius: 8, textAlign: 'center',
            fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
            color: T.muted, border: `1px solid ${T.border}`,
            background: 'rgba(255,255,255,.02)',
          }}>
            Current plan
          </div>
        ) : isLocked ? (
          <>
            {/* Waitlist flow */}
            {wlStep === 'done' && (
              <div style={{
                padding:'12px', borderRadius:8, textAlign:'center',
                background: plan.featured ? 'rgba(201,168,76,.08)' : 'rgba(46,204,138,.07)',
                border:`1px solid ${plan.featured ? 'rgba(201,168,76,.22)' : 'rgba(46,204,138,.2)'}`,
                animation:'slideDown .2s ease both',
              }}>
                <div style={{ fontSize:12, fontWeight:700, color: plan.featured ? T.gold : T.green, marginBottom:3 }}>
                  ✓ You're on the list
                </div>
                <div style={{ fontSize:11, color:T.muted }}>We'll email you before launch.</div>
              </div>
            )}
            {wlStep === 'info' && (
              <div style={{
                padding:'12px', borderRadius:8, textAlign:'center',
                background:'rgba(201,168,76,.06)', border:`1px solid rgba(201,168,76,.18)`,
                animation:'slideDown .2s ease both',
              }}>
                <div style={{ fontSize:11, color:T.gold, lineHeight:1.5 }}>{wlInfoMsg}</div>
              </div>
            )}
            {(wlStep === 'input' || wlStep === 'loading') && (
              <div style={{ display:'flex', flexDirection:'column', gap:7, marginBottom:8, animation:'slideDown .18s ease both' }}>
                <input
                  className="kfx-wl-input"
                  type="email" placeholder="your@email.com"
                  value={email} onChange={e => { setEmail(e.target.value); setWlErr(null); }}
                  onKeyDown={e => { if(e.key === 'Enter') handleWaitlist(); }}
                  autoFocus
                />
              </div>
            )}
            {wlStep !== 'done' && wlStep !== 'info' && (
              <button
                className={plan.featured ? 'kfx-btn-primary' : 'kfx-btn-ghost'}
                onClick={handleWaitlist}
                disabled={wlStep === 'loading'}
                style={{
                  display:'flex', alignItems:'center', justifyContent:'center', gap:7,
                  width:'100%', padding:'12px', borderRadius:8,
                  fontSize:11, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase',
                  cursor: wlStep === 'loading' ? 'not-allowed' : 'pointer',
                  border:'none', opacity: wlStep === 'loading' ? .65 : 1,
                  ...(plan.featured ? {
                    background:`linear-gradient(135deg,#D4B060 0%,#C9A84C 60%,#B8963D 100%)`,
                    color:'#080808',
                    boxShadow:`0 5px 18px rgba(201,168,76,.22)`,
                  } : {
                    background:'rgba(46,204,138,.08)',
                    color: T.green,
                    border:`1px solid rgba(46,204,138,.22)`,
                  }),
                }}
              >
                {wlStep === 'loading' ? <><Spinner/> Saving…</> : wlStep === 'input' ? `Notify me — ${plan.name}` : plan.cta}
              </button>
            )}
            {wlErr && (
              <div style={{ fontSize:11, color:'#FCA5A5', marginTop:6, textAlign:'center' }}>{wlErr}</div>
            )}
            <p style={{ fontSize:10.5, color:T.faint, textAlign:'center', margin:'8px 0 0' }}>
              Launching Q3 2026 · Early list gets locked pricing
            </p>
          </>
        ) : null}
      </div>

      {/* Divider */}
      <div style={{ height:1, background:T.border, marginBottom:18 }}/>

      {/* Features list */}
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color:T.goldLabel, marginBottom:12 }}>
        What's included
      </div>
      <div style={{ flex:1 }}>
        {plan.features.map((f, i) => {
          const text = typeof f === 'string' ? f : f.text;
          const note = typeof f === 'object' ? f.note : null;
          return (
            <div key={i} className="kfx-feat-row">
              <CheckIcon size={14} dimmed={isCurrent && i === plan.features.length - 1 && note}/>
              <span style={{ fontSize:12.5, color: T.textSub, lineHeight:1.55 }}>
                {text}
                {note && (
                  <span style={{
                    fontSize:10, color: T.gold, marginLeft:7, fontWeight:700,
                    background:'rgba(201,168,76,.10)', padding:'1px 6px', borderRadius:3, letterSpacing:'.04em',
                  }}>{note}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Comparison table ──────────────────────────────────────── */
function ComparisonTable() {
  const [openCats, setOpenCats] = useState(['Core', 'AI Coach']);
  const toggle = cat => setOpenCats(prev =>
    prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
  );

  return (
    <div style={{ maxWidth:740, margin:'0 auto', padding:'0 clamp(16px,4vw,48px) 64px' }}>
      {/* Section header */}
      <div style={{ textAlign:'center', marginBottom:32 }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:T.goldLabel, marginBottom:12 }}>
          Plan comparison
        </div>
        <h2 style={{
          fontSize:'clamp(20px,3vw,28px)', fontWeight:800, color:T.text,
          margin:0, letterSpacing:'-.03em', lineHeight:1.2,
        }}>
          Everything side by side.
        </h2>
      </div>

      <div className="kfx-tbl-wrap" style={{
        background:T.dark2, border:`1px solid ${T.border}`,
        borderRadius:12, overflow:'hidden',
      }}>
        {/* Header */}
        <div className="kfx-tbl-cols" style={{
          display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr',
          padding:'13px 20px',
          borderBottom:`1px solid rgba(255,255,255,.07)`,
          background:T.dark3,
          position:'sticky', top:0, zIndex:2,
        }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:T.muted }}>Feature</div>
          {[
            { label:'Free',     color:T.muted  },
            { label:'Pro',      color:T.gold,  sub:'Most popular' },
            { label:'Lifetime', color:T.green  },
          ].map(({ label, color, sub }) => (
            <div key={label} style={{ textAlign:'center' }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color }}>{label}</div>
              {sub && <div style={{ fontSize:8, color:`${color}55`, fontWeight:400, marginTop:1 }}>{sub}</div>}
            </div>
          ))}
        </div>

        {/* Accordion by category */}
        {TABLE.map(group => {
          const isOpen = openCats.includes(group.category);
          return (
            <div key={group.category}>
              {/* Category header — clickable */}
              <div
                className="kfx-tbl-cols"
                onClick={() => toggle(group.category)}
                style={{
                  display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr',
                  padding:'9px 20px',
                  background:'rgba(255,255,255,.018)',
                  borderBottom:`1px solid rgba(255,255,255,.05)`,
                  cursor:'pointer',
                  userSelect:'none',
                  transition:'background .12s',
                }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                  <span style={{ fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:T.goldLabel }}>
                    {group.category}
                  </span>
                  <span style={{
                    fontSize:12, color:T.muted,
                    transition:'transform .2s',
                    transform: isOpen ? 'rotate(90deg)' : 'none',
                    display:'inline-block', lineHeight:1,
                  }}>›</span>
                </div>
                <div/><div/><div/>
              </div>

              {/* Rows */}
              {isOpen && group.rows.map((row, i) => (
                <div
                  key={row.f}
                  className="kfx-tbl-row kfx-tbl-cols"
                  style={{
                    display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr',
                    padding:'10px 20px',
                    borderBottom:`1px solid ${T.borderSub}`,
                    background:'transparent',
                    animation:'slideDown .15s ease both',
                    animationDelay:`${i * 0.03}s`,
                  }}
                >
                  <span style={{ fontSize:12, color:T.textSub }}>{row.f}</span>
                  <TableCell val={row.free} plan="free"/>
                  <TableCell val={row.pro}  plan="pro"/>
                  <TableCell val={row.life} plan="life"/>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Main export ───────────────────────────────────────────── */
export default function PricingPage({ session, setPage }) {
  // Detect payment success redirect
  const success = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('success') === '1';

  // Derive current plan from session (adjust to your session shape)
  const currentPlan = session?.plan ?? session?.user?.plan ?? 'free';

  return (
    <div className="kfx-up-page" style={{ minHeight:'100vh', background:T.bg, color:T.text }}>
      <style>{CSS}</style>

      {/* ── Payment success ── */}
      {success && (
        <div style={{
          background:'rgba(61,190,115,.07)', border:'1px solid rgba(61,190,115,.2)',
          borderRadius:8, padding:'12px 20px', margin:'20px auto',
          maxWidth:540, textAlign:'center', fontSize:13, color:T.green, fontWeight:600,
        }}>
          ✓ Payment successful — your plan has been upgraded. Welcome to KFX Pro.
        </div>
      )}

      {/* ══════════════════════════════════════
          PAGE HEADER — context-aware, no hero
      ══════════════════════════════════════ */}
      <div style={{
        maxWidth:1060, margin:'0 auto',
        padding:'clamp(40px,6vw,72px) clamp(16px,4vw,48px) 0',
      }}>
        {/* Breadcrumb */}
        <div style={{
          display:'flex', alignItems:'center', gap:8,
          fontSize:11, color:T.muted, marginBottom:28,
        }}>
          <button
            onClick={() => typeof setPage === 'function' && setPage('d')}
            style={{ background:'none', border:'none', padding:0, cursor:'pointer', color:T.muted, fontFamily:'inherit', fontSize:'inherit' }}
          >
            Dashboard
          </button>
          <span style={{ opacity:.4 }}>/</span>
          <span style={{ color:T.textSub, fontWeight:500 }}>Upgrade</span>
        </div>

        {/* Header row */}
        <div style={{
          display:'flex', alignItems:'flex-end', justifyContent:'space-between',
          flexWrap:'wrap', gap:16, marginBottom:48,
        }}>
          <div>
            <h1 style={{
              fontSize:'clamp(24px,4vw,36px)', fontWeight:800,
              color:T.text, margin:'0 0 8px',
              letterSpacing:'-.03em', lineHeight:1.15,
            }}>
              Upgrade your plan
            </h1>
            <p style={{ fontSize:14, color:T.textSub, margin:0, lineHeight:1.6 }}>
              Pro and Lifetime are launching soon. Join the early list to lock in founder pricing.
            </p>
          </div>

          {/* Current plan pill */}
          <div style={{
            display:'inline-flex', alignItems:'center', gap:8,
            padding:'8px 14px', borderRadius:8,
            background:T.dark2, border:`1px solid ${T.border}`,
            flexShrink:0,
          }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background:T.green, boxShadow:`0 0 6px ${T.green}` }}/>
            <span style={{ fontSize:12, color:T.textSub }}>
              Current plan: <span style={{ fontWeight:700, color:T.text, textTransform:'capitalize' }}>{currentPlan}</span>
            </span>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          PLAN CARDS
      ══════════════════════════════════════ */}
      <div style={{ maxWidth:1060, margin:'0 auto', padding:'0 clamp(16px,4vw,48px) 72px' }}>
        <div className="kfx-plans-grid" style={{
          display:'grid', gridTemplateColumns:'repeat(3,1fr)',
          gap:16, alignItems:'start',
        }}>
          {PLANS.map(plan => (
            <PlanCard key={plan.id} plan={plan} currentPlan={currentPlan}/>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════
          COMPARISON TABLE
      ══════════════════════════════════════ */}
      <ComparisonTable/>

      {/* ══════════════════════════════════════
          FAQ
      ══════════════════════════════════════ */}
      <div style={{ maxWidth:580, margin:'0 auto', padding:'0 clamp(16px,4vw,48px) 96px' }}>
        <div style={{
          fontSize:10, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase',
          color:T.goldLabel, marginBottom:6,
        }}>
          Frequently asked
        </div>
        <p style={{ fontSize:11, color:T.muted, margin:'0 0 20px' }}>
          More questions?{' '}
          <a href="mailto:support@kfxjournal.com" style={{ color:T.gold, textDecoration:'none' }}>
            support@kfxjournal.com
          </a>
        </p>
        {FAQS.map(f => <FaqItem key={f.q} {...f}/>)}
      </div>

    </div>
  );
}